// Schemas for routes-softphone.ts (browser-safe zod). Advisor softphone in LIA on Telnyx (plan B3).
//
// Honesty rules encoded here:
//  • the softphone is «sin verificar» until a real Telnyx account completes an advisor call (capability `webrtc`);
//  • presence `offline` means "no softphone connected" (no recent heartbeat from a registered browser) — never guessed;
//  • the WebRTC login token is short-lived, returned once to the advisor's own browser and never stored by LIA;
//  • a call is only `connected` after Telnyx reported the prospect's leg answered and LIA bridged it to the advisor.
import { z } from 'zod';

const Iso = z.string();
const Id = z.string().min(1).max(64);

/** Presence the advisor chooses. `busy` (on a call) and `offline` (no softphone connected) are computed. */
export const SOFTPHONE_CHOSEN_PRESENCE = ['available', 'away'] as const;
export const SoftphoneChosenPresenceSchema = z.enum(SOFTPHONE_CHOSEN_PRESENCE);
export type SoftphoneChosenPresence = z.infer<typeof SoftphoneChosenPresenceSchema>;

export const SOFTPHONE_PRESENCE = ['available', 'busy', 'away', 'offline'] as const;
export const SoftphonePresenceSchema = z.enum(SOFTPHONE_PRESENCE);
export type SoftphonePresence = z.infer<typeof SoftphonePresenceSchema>;

export const SOFTPHONE_PRESENCE_LABELS: Record<SoftphonePresence, string> = {
  available: 'Disponible',
  busy: 'En llamada',
  away: 'Ausente',
  offline: 'Desconectado',
};

/**
 * Server view of a softphone call (the browser's audio state — muted/held — is local to the SDK):
 *  dialing    → LIA is ringing the advisor's own softphone (outbound: first leg) or the call is being placed;
 *  calling    → the advisor's leg is up and the prospect's phone is ringing (outbound);
 *  ringing    → inbound call offered to this advisor (ring group) — answer in the softphone;
 *  connected  → prospect answered and LIA bridged them to the advisor;
 *  ended      → finished (endReason says why).
 */
export const SOFTPHONE_CALL_STATES = ['dialing', 'calling', 'ringing', 'connected', 'ended'] as const;
export const SoftphoneCallStateSchema = z.enum(SOFTPHONE_CALL_STATES);
export type SoftphoneCallState = z.infer<typeof SoftphoneCallStateSchema>;

export const SoftphoneTransferSchema = z.object({
  /** ringing = the target is being called; answered = transferred; failed = the target did not answer / refused. */
  state: z.enum(['ringing', 'answered', 'failed']),
  targetLabel: z.string(),
  detail: z.string().nullable(),
});

export const SoftphoneCallSchema = z.object({
  /** Logical call id (the call shows in Llamadas and the contact's history as an advisor call). */
  callId: Id,
  direction: z.enum(['inbound', 'outbound']),
  state: SoftphoneCallStateSchema,
  contactId: Id.nullable(),
  contactName: z.string().nullable(),
  counterpartE164: z.string().nullable(),
  endpointId: Id.nullable(),
  lineName: z.string().nullable(),
  lineE164: z.string().nullable(),
  createdAt: Iso,
  answeredAt: Iso.nullable(),
  endedAt: Iso.nullable(),
  endReason: z.string().nullable(),
  /** Live copilot session of this call (B5 transcription), when the copilot module and the line allow it. */
  copilotSessionId: Id.nullable(),
  /** Why there is no copilot session (module off, line without live transcription…), null when there is one. */
  copilotDetail: z.string().nullable(),
  transfer: SoftphoneTransferSchema.nullable(),
});
export type SoftphoneCall = z.infer<typeof SoftphoneCallSchema>;

export const SoftphoneLineSchema = z.object({
  endpointId: Id,
  displayName: z.string(),
  phoneE164: z.string(),
  projectIds: z.array(Id),
});
export type SoftphoneLine = z.infer<typeof SoftphoneLineSchema>;

export const SoftphoneTransferTargetSchema = z.object({
  membershipId: Id,
  name: z.string(),
  presence: SoftphonePresenceSchema,
});

export const SoftphoneStateSchema = z.object({
  /** Seat enabled by an admin for this member (role ≥ advisor) with a provisioned Telnyx credential. */
  seat: z.object({
    enabled: z.boolean(),
    provisioned: z.boolean(),
    /** Why the softphone cannot be used right now (null = it can). Spanish, shown as is. */
    blocker: z.string().nullable(),
    connectionId: Id.nullable(),
  }),
  presence: SoftphonePresenceSchema,
  chosenPresence: SoftphoneChosenPresenceSchema,
  lastHeartbeatAt: Iso.nullable(),
  /** Seconds an inbound call rings the advisors before it becomes a missed-call task. */
  ringTimeoutSecs: z.number().int(),
  /** Lines this advisor may call from (active Telnyx lines of their projects on the seat's connection). */
  lines: z.array(SoftphoneLineSchema),
  /** The current call (or the last one, ended less than a minute ago, so the UI can show how it ended). */
  call: SoftphoneCallSchema.nullable(),
  /** Other advisors with a seat on the same connection (for transfers). */
  transferTargets: z.array(SoftphoneTransferTargetSchema),
  /** `webrtc` capability state of the seat's Telnyx connection: «sin verificar» until a real advisor call completes. */
  verification: z.object({ state: z.enum(['verified', 'unverified', 'failed', 'unsupported', 'unknown']), detail: z.string() }),
});
export type SoftphoneState = z.infer<typeof SoftphoneStateSchema>;

export const SoftphoneTokenSchema = z.object({
  /** Telnyx WebRTC login token (JWT) for `new TelnyxRTC({ login_token })`. Short-lived; never stored by LIA. */
  loginToken: z.string(),
  /** Expiry read from the token when it can be read (Telnyx documents ~24 h; unverified), else null. */
  expiresAt: Iso.nullable(),
  notice: z.string(),
});
export type SoftphoneToken = z.infer<typeof SoftphoneTokenSchema>;

export const SoftphoneHeartbeatSchema = z.object({
  /** The browser's Telnyx client is connected and registered (ready to receive calls). */
  registered: z.boolean(),
});

export const SetSoftphonePresenceSchema = z.object({ presence: SoftphoneChosenPresenceSchema });

export const StartSoftphoneCallSchema = z
  .object({
    endpointId: Id.optional(),
    contactId: Id.optional(),
    /** Number typed in the dial pad (E.164 or national; parsed with the workspace's country). */
    to: z.string().trim().min(3).max(32).optional(),
  })
  .refine((v) => !!v.contactId || !!v.to, { message: 'Indica un contacto o un número' });
export type StartSoftphoneCall = z.infer<typeof StartSoftphoneCallSchema>;

export const TransferSoftphoneCallSchema = z
  .object({ toMembershipId: Id.optional(), toNumber: z.string().trim().min(3).max(32).optional() })
  .refine((v) => !!v.toMembershipId !== !!v.toNumber, { message: 'Indica un asesor o un número (solo uno)' });

// ───────── Admin ─────────

export const SoftphoneConnectionSettingsSchema = z.object({
  connectionId: Id,
  label: z.string(),
  /** Telnyx Credential Connection where the advisors' telephony credentials live (owner creates it in Telnyx). */
  credentialConnectionId: z.string().nullable(),
  ringTimeoutSecs: z.number().int(),
});

export const SoftphoneSeatSchema = z.object({
  membershipId: Id,
  name: z.string(),
  email: z.string(),
  role: z.string(),
  enabled: z.boolean(),
  /** A Telnyx telephony credential exists for this seat (its secret is never stored or shown). */
  provisioned: z.boolean(),
  connectionId: Id.nullable(),
  presence: SoftphonePresenceSchema,
  lastHeartbeatAt: Iso.nullable(),
  detail: z.string().nullable(),
});
export type SoftphoneSeat = z.infer<typeof SoftphoneSeatSchema>;

export const SoftphoneAdminSchema = z.object({
  connections: z.array(SoftphoneConnectionSettingsSchema),
  seats: z.array(SoftphoneSeatSchema),
  notice: z.string(),
});
export type SoftphoneAdmin = z.infer<typeof SoftphoneAdminSchema>;

export const SOFTPHONE_RING_TIMEOUT = { min: 10, max: 90, default: 25 } as const;

export const UpdateSoftphoneSettingsSchema = z.object({
  credentialConnectionId: z.string().trim().min(1).max(120).regex(/^[A-Za-z0-9_-]+$/, 'Identificador de conexión inválido').nullable(),
  ringTimeoutSecs: z.number().int().min(SOFTPHONE_RING_TIMEOUT.min).max(SOFTPHONE_RING_TIMEOUT.max),
});

export const UpdateSoftphoneSeatSchema = z.object({
  enabled: z.boolean(),
  /** Telnyx connection of the seat (required when the workspace has several Telnyx connections). */
  connectionId: Id.optional(),
});
